//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import classNames from 'classnames';

import { RCText, RCIcon, useMediaQuery } from '../';
import {
  RCModalBase,
  RCModalBaseRootProps,
} from '../internalComponents/RCModalBase/RCModalBase';
import { RCModalActionProps, RCModalActionsProps } from '../internalComponents/RCModalBase/RCModalBaseActions';
import { RCIconProps } from '../RCIcon/RCIcon';


interface RCModalProps {
  /** Optionally provide a trigger to have the modal handle onClick behavior and state changes. */
  trigger?: JSX.Element;
  /** Can be used to control the state of the modal when paired with `onOpenChange`. */
  isOpen?: RCModalBaseRootProps['isOpen'];
  /** Can be used to control the state of the modal when paired with `isOpen`.
   *  Alternatively, can be used as a callback to track state changes.
  */
  onOpenChange?: RCModalBaseRootProps['onOpenChange'];
  /** The title of the modal. */
  title: string;
  /** Optionally display an icon above the title. Icon will be colored to match the theme of the primary action */
  icon?: RCIconProps['icon'];
  /** Actions to be appended at the end of the modal.
   *
   * Includes both `primary` and `secondary` actions by default, using default text values of `Save` and `Cancel` respectively.
   * Pass `secondary: false` for a single acknowledgement button. A `danger` primary always keeps its
   * `secondary`, which takes focus on open instead.
   */
  actions?: Omit<RCModalActionsProps, 'tertiary' | 'align' | 'primary'> & {
    primary?: true | RCModalActionProps;
  };
}

export function RCModalConfirmation({
  trigger = undefined,
  isOpen = undefined,
  onOpenChange = undefined,
  title,
  icon = undefined,
  actions = {},
  children = undefined,
}: React.PropsWithChildren<RCModalProps>) {
  const { primary, secondary, size } = actions;
  const primaryAction = typeof primary === 'object' ? primary : {};
  const secondaryAction = typeof secondary === 'object' ? secondary : {};
  const isDanger = !!primaryAction.variant?.includes('danger');
  const hasSecondary = secondary !== false || isDanger;

  React.useEffect(() => {
    if (secondary === false && isDanger && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        'RCModalConfirmation: a `danger` primary always keeps its secondary action, so `secondary: false` is ignored.',
      );
    }
  }, [secondary, isDanger]);

  function shouldFocusPrimary() {
    if (isDanger) return false;
    if (!hasSecondary) return true;
    if (secondaryAction.initialFocus === true) return false;
    if (primaryAction.initialFocus === false) return false;
    return true;
  }

  function getInitialFocus(bool: boolean) {
    if (bool) return { initialFocus: true };
    return {};
  }
  const focusPrimary = getInitialFocus(shouldFocusPrimary());
  const focusSecondary = getInitialFocus(!shouldFocusPrimary());

  const modifiedActions: RCModalActionsProps = {
    align: { desktop: 'center', tablet: 'stretch' },
    size,
    primary: { ...primaryAction, ...focusPrimary },
    secondary: hasSecondary && { ...secondaryAction, ...focusSecondary },
  };

  const isMobile = useMediaQuery('mobile');
  const actionTheme = (
    primaryAction.variant === 'loud' ? 'loud' :
      primaryAction.variant?.includes('danger') ? 'danger' :
        undefined
  );
  return (
    <RCModalBase.Root
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      role="alertdialog"
      useDismissProps={{ outsidePress: false }}
    >
      {trigger && <RCModalBase.Trigger>{trigger}</RCModalBase.Trigger>}
      <RCModalBase.Content
        className="rc-modal-confirmation"
        position={isMobile ? 'bottom' : undefined}
      >
        <div className="rc-modal-confirmation__main">
          {icon &&
            <div className={classNames(
              'rc-modal-confirmation__icon',
              { [`rc-modal-confirmation__icon--${actionTheme}`]: actionTheme },
            )}>
              <RCIcon icon={icon} />
            </div>
          }
          <div className="rc-modal-confirmation__text">
            <RCModalBase.Heading className="rc-modal-confirmation__title">
              <RCText.Title size="600" htmlTag="span">
                {title}
              </RCText.Title>
            </RCModalBase.Heading>
            <RCModalBase.Description className="rc-modal-confirmation__description">
              <RCText>{children}</RCText>
            </RCModalBase.Description>
          </div>
        </div>
        <div className="rc-modal-confirmation__actions">
          <RCModalBase.Actions {...modifiedActions} />
        </div>
      </RCModalBase.Content>
    </RCModalBase.Root>
  );
}
